import { IconBluetooth, IconPlugConnected, IconPlugConnectedX, IconAlertTriangle, IconWifi } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { BleStatusIndicator } from "./ble-status-indicator"
import { getBrowserCompatibilityMessage } from "@/lib/ble/constants"
import type { BleState } from "@/types/device"

export type DataSource = "bluetooth" | "http"

interface BleConnectionPanelProps {
  state: BleState
  isSupported: boolean
  isHttpConnected?: boolean
  dataSource: DataSource
  onDataSourceChange: (source: DataSource) => void
  onScan: () => void
  onDisconnect: () => void
}

export function BleConnectionPanel({
  state,
  isSupported,
  isHttpConnected = false,
  dataSource,
  onDataSourceChange,
  onScan,
  onDisconnect,
}: BleConnectionPanelProps) {
  const compatibilityMessage = getBrowserCompatibilityMessage()
  const isBleConnected = state.status === "connected"
  const isScanning = state.status === "scanning"
  const isConnecting = state.status === "connecting"
  const isBusy = isScanning || isConnecting

  const isConnected = dataSource === "bluetooth" ? isBleConnected : isHttpConnected

  if (!isSupported || compatibilityMessage) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <IconAlertTriangle className="size-5 text-yellow-500" />
            <CardTitle className="text-lg">Browser Not Supported</CardTitle>
          </div>
          <CardDescription>
            {compatibilityMessage ?? "Web Bluetooth is not available in this browser."}
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg">Data Source</CardTitle>
          </div>
          {/* Connection Status */}
          <div className="flex items-center gap-1.5">
            <span className="relative flex size-2">
              <span
                className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${
                  isConnected ? "bg-green-400" : "bg-red-400"
                }`}
              />
              <span
                className={`relative inline-flex size-2 rounded-full ${
                  isConnected ? "bg-green-500" : "bg-red-500"
                }`}
              />
            </span>
            <span className={`text-xs font-medium ${isConnected ? "text-green-600" : "text-red-600"}`}>
              {isConnected ? "Connected" : "Disconnected"}
            </span>
          </div>
        </div>
        <CardDescription>
          {dataSource === "bluetooth"
            ? isBleConnected
              ? `Connected to ${state.device?.name ?? "device"}`
              : "Connect via Bluetooth to receive data"
            : isHttpConnected
              ? "Receiving data via HTTP"
              : "No recent HTTP data received"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Data Source Toggle */}
        <Tabs value={dataSource} onValueChange={(v) => onDataSourceChange(v as DataSource)}>
          <TabsList className="w-full">
            <TabsTrigger value="bluetooth" className="flex-1 gap-2">
              <IconBluetooth className="size-4" />
              Bluetooth
            </TabsTrigger>
            <TabsTrigger value="http" className="flex-1 gap-2">
              <IconWifi className="size-4" />
              HTTP
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Bluetooth Controls (only shown when bluetooth selected) */}
        {dataSource === "bluetooth" && (
          <div className={`flex items-center p-3 rounded-lg bg-muted/50 ${isBleConnected || isBusy ? "justify-between" : "justify-center"}`}>
            {(isBleConnected || isBusy) && <BleStatusIndicator status={state.status} />}
            {isBleConnected ? (
              <Button variant="outline" size="sm" onClick={onDisconnect} className="w-full sm:w-auto">
                <IconPlugConnectedX className="size-3" />
                Disconnect
              </Button>
            ) : (
              <Button size="sm" onClick={onScan} disabled={isBusy} className="w-full sm:w-auto">
                <IconPlugConnected className="size-3" />
                {isBusy ? "Scanning..." : "Scan for Device"}
              </Button>
            )}
          </div>
        )}

        {/* HTTP Status (only shown when http selected) */}
        {dataSource === "http" && (
          <div className="flex items-center justify-center p-3 rounded-lg bg-muted/50">
            <span className="text-sm text-muted-foreground">
              {isHttpConnected
                ? "Receiving readings from device API"
                : "Waiting for device to send readings..."}
            </span>
          </div>
        )}

        {state.error && dataSource === "bluetooth" && (
          <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
            {state.error}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
